import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { formatNullableString, logError, toNullableLocaleDateTimeString } from '../utilities';
import { QuoteRequestResponse } from '../apiModels/quoteRequestResponse';
import { quoteContactMethods } from '../apiModels/quoteContactMethod';
import { ShipmentUpdateModel } from '../apiModels/shipmentUpdateModel';
import { ShipmentStatusModel } from '../apiModels/shipmentStatusModel';
import { Value } from 'react-datetime-picker/dist/cjs/shared/types';
import { editShipmentUpdate, getStatusOptions } from '../api/shipmentUpdateApi';
import { toast } from 'react-toastify';
import ShipmentUpdateEditForm from './ShipmentUpdateEditForm';
import LoadingIcon from './LoadingIcon';

export default function ShipmentUpdateEditModal(props: { 
    show: boolean, 
    setShow: (show: boolean) => void, 
    shipmentUpdate: ShipmentUpdateModel,
    postSaveActions?: () => void
}) {
    const notifyUserOfError = (err: string) => toast(err, { type: 'error' });
    const notifyUserOfSuccess = (msg: string) => toast(msg, { type: 'success' });

    const [shipmentStatuses, setShipmentStatuses] = useState<ShipmentStatusModel[]>([]);
    const [description, setDescription] = useState(props.shipmentUpdate.description ?? '');
    const [location, setLocation] = useState(props.shipmentUpdate.location ?? '');
    const [statusId, setStatusId] = useState(props.shipmentUpdate.shipmentStatusId)
    const [updateTime, setUpdateTime] = useState<Value>(props.shipmentUpdate.updateTime);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const loadShipmentStatuses = async () => {
        setIsLoading(true);

        try {
            const st = await getStatusOptions();

            setShipmentStatuses(st);
            setIsLoading(false);
        }
        catch (error: any) {
            if (error instanceof Error) {
                notifyUserOfError(error.message);
            }

            logError(error);
            setIsLoading(false);
        }
    };

    const saveUpdate = async () => {
        setIsSaving(true);

        if (updateTime === null) {
            notifyUserOfError('You must select a date and time for this status update.');
            setIsSaving(false);
            return;
        }

        try {
            const s = await editShipmentUpdate({
                shipmentUpdateId: props.shipmentUpdate.shipmentUpdateId,
                description: description,
                location: location,
                shipmentStatusId: statusId,
                updateTime: updateTime,
                shipmentTrackingNumber: props.shipmentUpdate.shipmentTrackingNumber
            });

            setIsSaving(false);
            notifyUserOfSuccess('Update saved successfully.');
            props.postSaveActions?.();
            props.setShow(false);
        }
        catch (error: any) {
            if (error instanceof Error) {
                notifyUserOfError(error.message);
            }

            logError(error);
            setIsSaving(false);
        }
    };

    useEffect(() => {
        loadShipmentStatuses();
    }, []);

    return (
        <>
            <Modal show={props.show} onHide={() => props.setShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Shipment Tracking Update - Edit</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                {isLoading && <LoadingIcon />}
                {!isLoading &&
                    <ShipmentUpdateEditForm shipmentTrackingNumber={props.shipmentUpdate.shipmentTrackingNumber}
                        shipmentStatuses={shipmentStatuses} 
                        description={description} 
                        setDescription={setDescription} 
                        location={location} 
                        setLocation={setLocation} 
                        statusId={statusId} 
                        setStatusId={setStatusId} 
                        updateTime={updateTime} 
                        setUpdateTime={setUpdateTime} />}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className='btn btn-default'
                        onClick={() => props.setShow(false)}>
                        Close
                    </button>
                    <button
                        className='btn btn-default'
                        onClick={saveUpdate}>
                        Save
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export const ButtonLoadingSpinner = () => {
    return (
        <div className="spinner-border spinner-border-sm text-default" role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
    );
};